import { useEffect, useMemo, useState, type FC } from "react";
import { SecondaryButton } from "../SecondaryButton";
import "./PlotCard.less";
import { type PlotCardProps } from "../../../pages/new-trail/types";
import { BufferZonePopup } from "./popUp/bufferZonePopup";
import { TrialPlotsPopup } from "./popUp/trialPopup";
import { usePlotCardState } from "./utils";
import { RenderCards } from "./renderCards";
import { DeletePlotModal } from "../../../pages/new-trail/deletePlotModal";
import { Button } from "syngenta-digital-cropwise-react-ui-kit";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import useHydratePlotDesign from "../../../pages/new-trail/hooks/useHydratePlotDesign";
import { useParams } from "react-router-dom";
import { StripsPopup } from "./popUp/StripsPopup";
import { AbLinePopup } from "./popUp/abLinePopup";
import { formatTranslation } from "../../utils/translationUtils";
import { useTranslation } from "react-i18next";

export const PlotCard: FC<PlotCardProps> = ({ data, trialData }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeAbLinePopupId, setActiveAbLinePopupId] = useState<number | null>(
    null
  );
  const [viewMode, setViewMode] = useState<"plots" | "strips">("plots"); // Default to plots, will be updated if strips data exists

  const { newTrialState, setNewTrialState, trialProtocolData } =
    useNewTrialState();
  const { id } = useParams();

  /* strips-flow: identify strip/plot type
  check protocol details to identify and update isStripType flag
   */

  // Determine if this is a strip type based on protocol
  const isStripType = trialProtocolData?.trial_application_method === "strip";
  const isTrialPlotsCard = data.name === "Trial Plots";
  const isStripsView = isStripType && isTrialPlotsCard && viewMode === "strips";
  const {
    contextHolder,
    handleAddData,
    toggleVisibility,
    handleBufferZoneData,
    handleTrailPlotsData,
    deleteData,
    setDeleteData,
    handleDeleteData,
  } = usePlotCardState();

  const handleAddAbLine = (): void => {
    const createdAbLineId = handleAddData(data);
    setActiveAbLinePopupId(createdAbLineId);
  };

  // 🧪 TESTING: Monitor strips data changes and auto-switch to strips mode
  useEffect(() => {
    // Auto-switch to strips mode when strips data is detected
    if (newTrialState?.stripsData?.isStripMode && viewMode !== "strips") {
      setViewMode("strips");
    }
    // Only auto-switch to plots mode if we explicitly don't have strip mode data
    // and we're not in the middle of creating strips (avoid premature switching)
    else if (
      newTrialState?.stripsData &&
      !newTrialState.stripsData.isStripMode &&
      viewMode === "strips"
    ) {
      setViewMode("plots");
    }
  }, [newTrialState?.stripsData?.isStripMode, viewMode]);

  // Set mode based on protocol trial_application_method
  useEffect(() => {
    if (trialProtocolData?.trial_application_method) {
      const protocolMode =
        trialProtocolData.trial_application_method === "strip"
          ? "strips"
          : "plots";

      setViewMode(protocolMode);
    }
  }, [trialProtocolData?.trial_application_method]);

  // Determine effective view mode - now based on protocol instead of manual toggle
  const effectiveViewMode = (() => {
    // If protocol defines the mode, use it
    if (trialProtocolData?.trial_application_method) {
      return trialProtocolData.trial_application_method === "strip"
        ? "strips"
        : "plots";
    }
    // Otherwise use the current viewMode
    return viewMode ?? "plots";
  })();

  useEffect(() => {
    const gridData: any = {
      id: Date.now(),
      name: "Machinery Grid",
      key: "Machinery Grid",
      width: 50,
      rotation: 0,
      visible: true,
    };
    setNewTrialState((prevState: any) => ({
      ...prevState,
      machinery_grid: [gridData],
    }));
  }, []);

  // Rebuild Plot Design state from a saved trial. Shared with the standalone
  // Rates & Dosage route, which has no Plot Design UI to hang it off.
  //
  // Registered AFTER the default-machinery-grid initializer above, and that
  // order is load-bearing: effects run in declaration order, so a hook placed
  // higher up would hydrate the saved grid only for the `[]` initializer to
  // immediately overwrite its width/rotation with the 50/0 default. This
  // mirrors where the hydration effect sat before it was extracted.
  useHydratePlotDesign(id ? trialData?.trialData : undefined);

  const cardsToRender = useMemo(() => {
    if (data.name === "A/B Line + Machinery Grid") {
      return (newTrialState?.ab_line ?? []).filter(
        (line: any) => line?.key === "A/B Line + Machinery Grid"
      );
    }

    const sourceCards = (newTrialState as any)[data.key as string];
    if (!Array.isArray(sourceCards) || sourceCards.length === 0) {
      return [];
    }

    return sourceCards.filter((val: any) => {
      if (val && typeof val === "object") {
        if ("plot" in val && val.plot.key === data.name) {
          return true;
        }
        if ("key" in val && val.key === data.name) {
          return true;
        }
      }
      return false;
    });
  }, [data.key, data.name, newTrialState]);

  return (
    <>
      {contextHolder}
      <div className="plot-card-main" data-testid="plot-card" key={data.id}>
        <div className="head-sec">
          {data.name !== "Machinery Grid" && (
            <div className="title-sec">
              <img src={data?.icon} alt="plot-icon" />
              <div>
                <h3 className="card-title">
                  {isStripsView ? "Strip" : data?.name}
                </h3>
                <p className="card-description">
                  {isStripsView
                    ? formatTranslation(t("trail.stripeDescription"))
                    : data?.description}
                </p>
                <span></span>
              </div>
            </div>
          )}

          {data.name === "A/B Line + Machinery Grid" && (
            <AbLinePopup
              open={Boolean(activeAbLinePopupId)}
              abLineId={activeAbLinePopupId}
              onClose={() => {
                // If the line still has the draft key, Apply was not clicked — discard it.
                setNewTrialState((prevState: any) => ({
                  ...prevState,
                  ab_line: (prevState.ab_line ?? []).filter(
                    (line: any) =>
                      !(
                        line.id === activeAbLinePopupId &&
                        line.key === "__AB_DRAFT__"
                      )
                  ),
                }));
                setActiveAbLinePopupId(null);
              }}
            >
              <Button
                className="plus-btn"
                onClick={handleAddAbLine}
                disabled={Boolean(activeAbLinePopupId)}
              >
                <SecondaryButton>+</SecondaryButton>
              </Button>
            </AbLinePopup>
          )}

          {data.name === "Buffer Zone" && (
            <BufferZonePopup
              onHandleData={handleBufferZoneData}
              openPopoverKey="openPopover"
              val={data.id}
              trialData={trialData}
            >
              <Button
                className={`plus-btn ${
                  newTrialState.buffer_zone?.length >= 1 ? "disabled" : ""
                }`}
                disabled={newTrialState.buffer_zone?.length >= 1}
              >
                <SecondaryButton>+</SecondaryButton>
              </Button>
            </BufferZonePopup>
          )}

          {/* {data.name === "Machinery Grid" && (
            <MachineryGridPopup
              openPopoverKey="openPopover"
              onHandleData={handleMachineGridData}
              val={data.id}
              trialData={trialData}
            >
              <Button
                className={`plus-btn ${
                  newTrialState.machinery_grid?.length >= 1 ? "disabled" : ""
                }`}
                disabled={newTrialState.machinery_grid?.length >= 1}
              >
                <SecondaryButton>+</SecondaryButton>
              </Button>
            </MachineryGridPopup>
          )} */}

          {data.name === "Trial Plots" && (
            <>
              {/* Toggle commented out - mode now determined by protocol */}
              {/* {renderModeToggle()} */}
              {isStripsView ? (
                <StripsPopup
                  openPopoverKey="openPopover"
                  onHandleData={handleTrailPlotsData}
                  val={data.id}
                >
                  <Button
                    className={`plus-btn ${
                      newTrialState?.stripsData?.stripDetails &&
                      newTrialState.stripsData.stripDetails.length > 0
                        ? "disabled"
                        : ""
                    }`}
                    disabled={
                      newTrialState?.stripsData?.stripDetails &&
                      newTrialState.stripsData.stripDetails.length > 0
                    }
                  >
                    <SecondaryButton>+</SecondaryButton>
                  </Button>
                </StripsPopup>
              ) : (
                <TrialPlotsPopup
                  openPopoverKey="openPopover"
                  onHandleData={handleTrailPlotsData}
                  val={data.id}
                >
                  <div className="plus-btn">
                    <SecondaryButton>+</SecondaryButton>
                  </div>
                </TrialPlotsPopup>
              )}
            </>
          )}
        </div>

        <div>
          {cardsToRender.length > 0 && (
            <RenderCards
              key={data.key}
              cards={cardsToRender}
              toggleVisibility={toggleVisibility}
              data={data}
              setIsModalOpen={setIsModalOpen}
              setDeleteData={setDeleteData}
              trialData={trialData}
              isStripType={isStripType}
              viewMode={effectiveViewMode}
              onViewModeChange={setViewMode}
            />
          )}
        </div>
      </div>
      <DeletePlotModal
        dataKey={data.key}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        data={deleteData}
        onDelete={handleDeleteData}
      />
    </>
  );
};
